import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import CtaBlock from "../components/CtaBlock";
import FaqSection, { faqJsonLd } from "../components/FaqSection";
import ContextualLinks from "../components/ContextualLinks";
import { ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * Shared template for /ai-visibility-audit, /chatgpt-visibility,
 * /construction-ai-visibility, /about-ai-visibility etc.
 *
 * Props (all optional except meta + h1):
 *   meta: { title, description, canonical, ogImage? }
 *   eyebrow: small uppercase tag above the H1
 *   h1, subhead
 *   intro: string (1-2 paragraphs HTML)
 *   sections: [{ heading, body, bullets?, image? }, ...]
 *   bullets: a list of items to render as a "Why this matters" section
 *   faqs: [{ q, a, aPlain? }, ...]
 *   relatedLinks: [{ title, desc, href }, ...]
 *   testId: string
 *   structuredData: extra JSON-LD blobs to inject (Service / LocalBusiness etc)
 */
export default function LandingTemplate({
  meta = {},
  eyebrow,
  h1,
  subhead,
  intro,
  sections = [],
  bullets,
  faqs = [],
  relatedLinks = [],
  testId,
  structuredData = [],
}) {
  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid={testId || "landing-page"}>
      <Helmet>
        {meta.title && <title>{meta.title}</title>}
        {meta.description && <meta name="description" content={meta.description} />}
        {meta.canonical && <link rel="canonical" href={meta.canonical} />}
        {meta.title && <meta property="og:title" content={meta.title} />}
        {meta.description && <meta property="og:description" content={meta.description} />}
        <meta property="og:type" content="website" />
        {meta.canonical && <meta property="og:url" content={meta.canonical} />}
        {meta.ogImage && <meta property="og:image" content={meta.ogImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        {meta.title && <meta name="twitter:title" content={meta.title} />}
        {meta.description && <meta name="twitter:description" content={meta.description} />}
        {faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(faqJsonLd(faqs))}</script>
        )}
        {structuredData.map((blob, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(blob)}</script>
        ))}
      </Helmet>

      {/* HERO */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          {eyebrow && (
            <p className="font-mono-eav text-[11px] tracking-[0.24em] uppercase text-[#b8963e]">
              {eyebrow}
            </p>
          )}
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#f4efe6] leading-[1.06]"
              style={{ fontWeight: 500 }}>
            {h1}
          </h1>
          {subhead && (
            <p className="mt-6 max-w-3xl text-lg sm:text-xl text-[#c9bca4] leading-relaxed">
              {subhead}
            </p>
          )}
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="https://theentityagency.com" data-testid="landing-primary-cta"
                className="btn-gold">
              Request a Free Snapshot <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/research/methodology"
                  data-testid="landing-secondary-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#2a2620] hover:border-[#b8963e] text-sm text-[#c9bca4] rounded-sm transition-colors">
              How we score
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      {intro && (
        <section className="border-b border-[#2a2620]">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
            <div className="research-body text-[#c9bca4]"
                  dangerouslySetInnerHTML={{ __html: intro }} />
          </div>
        </section>
      )}

      {/* BULLETS */}
      {bullets && bullets.items && bullets.items.length > 0 && (
        <section className="border-b border-[#2a2620]">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#f4efe6]">
              {bullets.title || "What you'll see"}
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5">
              {bullets.items.map((b, i) => (
                <div key={i} className="flex gap-3" data-testid={`landing-bullet-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#b8963e] shrink-0 mt-1" />
                  <p className="text-[#c9bca4] leading-relaxed">
                    {b.title && <strong className="text-[#f4efe6] block">{b.title}</strong>}
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTIONS */}
      {sections.map((s, i) => (
        <section key={i} className="border-b border-[#2a2620]" data-testid={`landing-section-${i}`}>
          <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#f4efe6]">{s.heading}</h2>
            <div className="mt-6 research-body text-[#c9bca4]"
                  dangerouslySetInnerHTML={{ __html: s.body }} />
            {s.bullets && (
              <ul className="mt-6 space-y-3">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex gap-3 text-[#c9bca4]">
                    <CheckCircle2 className="w-5 h-5 text-[#b8963e] shrink-0 mt-1" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}

      {faqs.length > 0 && <FaqSection items={faqs} testId={`${testId}-faq`} />}

      <CtaBlock testId={`${testId}-cta`} variant="panel" />

      {relatedLinks.length > 0 && (
        <ContextualLinks links={relatedLinks} testId={`${testId}-related`} />
      )}
    </div>
  );
}
